import { useTranslation } from 'react-i18next';
import { cn } from '../../common/functions/cn';
import useTestTag from '../../common/hooks/use-test-tag';
import { ButtonV2 } from '../button-v2/button-v2';
import { useScreenSize } from '../common-hooks';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/popover';
import {
  DynamicFormButtonLocation,
  DynamicFormButtonPosition,
  type DynamicFormButtonSetting,
} from './dynamic-form.interfaces';
import DynamicFormButton from './dynamic-form-button.component';

/**
 * Determines whether a button should render as a standalone element in the
 * mobile layout (outside the grouped workflow popover).
 *
 * Prefers the explicit `mobilePlacement` field when set; otherwise falls back
 * to the `name === 'save'` convention for backward compatibility.
 */
export function isStandaloneMobileButton(button: DynamicFormButtonSetting): boolean {
  if (button.mobilePlacement !== undefined) {
    return button.mobilePlacement === 'standalone';
  }
  return button.name === 'save';
}

interface DynamicFormButtonsProps {
  buttons: DynamicFormButtonSetting[];
  buttonStyles?: string;
  buttonsLocation?: DynamicFormButtonLocation;
  buttonsPosition?: DynamicFormButtonPosition;
  readOnly?: boolean;
}

const DynamicFormButtons = ({
  buttons,
  buttonStyles,
  buttonsLocation = DynamicFormButtonLocation.Left,
  buttonsPosition = DynamicFormButtonPosition.Inline,
  readOnly,
}: DynamicFormButtonsProps) => {
  const tagCreator = useTestTag('dynamic-form');
  const { isMobile } = useScreenSize();
  const { t } = useTranslation();

  const containerClassName = cn(
    'flex max-h-28 shrink-0 gap-x-2 gap-y-4 p-1',
    {
      'overflow-y-auto': !isMobile,
      'justify-end': buttonsLocation === DynamicFormButtonLocation.Right,
      'items-stretch border-border border-t border-solid py-2': buttonsPosition === DynamicFormButtonPosition.Fixed,
    },
    buttonStyles
  );

  if (isMobile) {
    const standaloneButtons = buttons.filter(isStandaloneMobileButton);
    const groupedButtons = buttons.filter((button) => !isStandaloneMobileButton(button));
    const visibleGroupedCount = groupedButtons.filter((button) => !button.hidden).length;

    return (
      <div ref={tagCreator('all-button')} className={containerClassName}>
        {/* When more than 1 workflow step, show them in a popover */}
        {visibleGroupedCount > 1 ? (
          <Popover>
            <PopoverTrigger
              className="min-w-0 flex-1"
              render={
                <ButtonV2 className="w-full" disabled={readOnly}>
                  {t('submit')}
                </ButtonV2>
              }
            />
            <PopoverContent side="top" align="center">
              {groupedButtons.map((button) => (
                <DynamicFormButton key={`button_${button.id}_${button.name}`} button={button} readOnly={readOnly} />
              ))}
            </PopoverContent>
          </Popover>
        ) : visibleGroupedCount === 1 ? (
          // When only 1 visible workflow button, just render them all (only the visible one will be shown)
          <div className="min-w-0 flex-1">
            {groupedButtons.map((button) => (
              <DynamicFormButton
                key={`button_${button.id}_${button.name}`}
                button={button}
                readOnly={readOnly}
                fullWidth
              />
            ))}
          </div>
        ) : null}
        {/* Save button, if present, should always be rendered separately */}
        {standaloneButtons.map((button) => (
          <DynamicFormButton key={`button_${button.id}_${button.name}`} button={button} readOnly={readOnly} />
        ))}
      </div>
    );
  }

  // Desktop view
  return (
    <div ref={tagCreator('button-container')} className={containerClassName}>
      {buttons.map((button) => (
        <DynamicFormButton key={`button_${button.id}_${button.name}`} button={button} readOnly={readOnly} />
      ))}
    </div>
  );
};

export default DynamicFormButtons;
